import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import alertService from '../services/sweetAlert';
import { animated, useSpring, useTrail, config } from 'react-spring';
import profileService from "../services/profile.service";

// Gift data with categories
const GIFTS = {
  basic: [
    { id: 1, name: 'Silver', icon: 'gold.png', cost: 1, emoji: '🪙' },
    { id: 3, name: 'Rose', icon: 'rose.svg', cost: 2, emoji: '🌹' },
    { id: 4, name: 'Chocolate', icon: 'chocolate-bar.svg', cost: 3, emoji: '🍫' },
    { id: 9, name: 'Tea', icon: 'tea.svg', cost: 2, emoji: '🍵' },
  ],
  premium: [
    { id: 10, name: 'Jeep', icon: 'jeep.svg', cost: 25000, emoji: '🚙' },
    { id: 11, name: 'Car', icon: 'car.svg', cost: 50000, emoji: '🚗' },
    { id: 14, name: 'SUV', icon: 'suv-car.svg', cost: 500000, emoji: '🚐' },
    { id: 15, name: 'Sports Car', icon: 'sport-car.svg', cost: 1000000, emoji: '🏎️' },
  ]
};

const CDN = 'https://files.geetsuhane.com/gifts/';

// Quantity options
const QUANTITIES = [1, 10, 100, 1000, 10000];

export default function Gifts({ giftItem, setgiftItem }) {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [selectedGift, setSelectedGift] = useState(GIFTS.basic[0]);
  const [quantity, setQuantity] = useState(1);
  const [gold, setGold] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [activeCategory, setActiveCategory] = useState('basic');

  // Animation for gift flying up
  const flyAnimation = useSpring({
    from: {
      transform: 'translateY(0px) scale(1)',
      opacity: 1
    },
    to: {
      transform: isSending ? 'translateY(-300px) scale(0.5)' : 'translateY(0px) scale(1)',
      opacity: isSending ? 0 : 1
    },
    config: { tension: 200, friction: 20 },
    onRest: () => {
      if (isSending) {
        setShowCelebration(true);
        setTimeout(() => {
          setShowCelebration(false);
          setIsSending(false);
        }, 1500);
      }
    }
  });

  // Trail animation for gift items
  const allGifts = [...GIFTS.basic, ...GIFTS.premium];
  const trail = useTrail(allGifts.length, {
    from: { opacity: 0, transform: 'scale(0.8)' },
    to: { opacity: 1, transform: 'scale(1)' },
    config: config.gentle
  });

  // Celebration particles
  const celebrationParticles = useTrail(12, {
    from: {
      opacity: 1,
      transform: 'translate(0px, 0px) scale(1)'
    },
    to: {
      opacity: showCelebration ? 0 : 1,
      transform: showCelebration
        ? `translate(${Math.random() * 200 - 100}px, ${-Math.random() * 150 - 50}px) scale(0)`
        : 'translate(0px, 0px) scale(1)'
    },
    config: { tension: 150, friction: 12 }
  });

  const getGold = async () => {
    if (!currentUser?.SID) return;
    const goldStatus = await profileService.getResult({
      qry: `CALL sp_getGoldBalance('${currentUser?.SID}');`
    });
    const goldValue = goldStatus?.[0][0]?.gold || 0;
    setGold(goldValue);
    localStorage.setItem("gold", goldValue);
  };

  const updateGifts = async () => {
    await profileService.getResult({
      qry: `CALL sp_insertGiftxn_V1('${currentUser.id}','${localStorage.getItem('uid')}','${selectedGift.id}','${localStorage.getItem('giftsource')}',${localStorage.getItem('item')},${selectedGift.cost * quantity},${quantity},@res)`
    });
    getGold();
  };

  const throwGift = () => {
    const currentTime = Date.now();
    if (currentTime - lastTapTime < 1000) {
      alertService.error("Slow down!", "You're clicking too fast!");
      return;
    }
    setLastTapTime(currentTime);

    const totalCost = selectedGift.cost * quantity;
    const currentGold = parseInt(localStorage.getItem("gold") || gold || 0);

    if (totalCost > currentGold) {
      alertService.error(
        'Not Enough Coins!',
        `You need ${totalCost.toLocaleString()} GS Coins but only have ${currentGold.toLocaleString()}`
      );
      navigate("/income");
      return;
    }

    setIsSending(true);
    updateGifts();
    setgiftItem(giftItem + 1);
  };

  useEffect(() => {
    if (currentUser?.id) {
      getGold();
    } else {
      navigate("/login");
    }
  }, []);

  const formatCost = (cost) => {
    if (cost >= 1000000) return (cost / 1000000) + 'M';
    if (cost >= 1000) return (cost / 1000) + 'K';
    return cost;
  };

  const totalCost = selectedGift.cost * quantity;

  return (
    <div style={{ padding: '16px', maxWidth: '400px', margin: '0 auto' }}>
      {/* Header with balance */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: 'white'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '500' }}>Your Balance</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <img
            src={CDN + 'gold.png'}
            alt="coin"
            style={{ width: '20px', height: '20px' }}
          />
          <span style={{ fontSize: '18px', fontWeight: '700' }}>
            {parseInt(localStorage.getItem("gold") || gold || 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px'
      }}>
        {['basic', 'premium'].map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              borderRadius: '10px',
              background: activeCategory === cat
                ? 'linear-gradient(135deg, #ff416c, #ff4b2b)'
                : '#f0f0f0',
              color: activeCategory === cat ? 'white' : '#666',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textTransform: 'capitalize'
            }}
          >
            {cat === 'basic' ? '🎁 Basic' : '🚗 Premium'}
          </button>
        ))}
      </div>

      {/* Gift Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px',
        marginBottom: '20px'
      }}>
        {GIFTS[activeCategory].map((gift, index) => (
          <animated.div
            key={gift.id}
            onClick={() => setSelectedGift(gift)}
            style={{
              ...trail[activeCategory === 'basic' ? index : index + 4],
              padding: '12px 8px',
              borderRadius: '12px',
              background: selectedGift.id === gift.id
                ? 'linear-gradient(135deg, #ff416c, #ff4b2b)'
                : '#f8f9fa',
              border: selectedGift.id === gift.id
                ? '2px solid #ff416c'
                : '2px solid transparent',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease',
              transform: selectedGift.id === gift.id ? 'scale(1.05)' : 'scale(1)'
            }}
          >
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>
              {gift.emoji}
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '600',
              color: selectedGift.id === gift.id ? 'white' : '#333',
              marginBottom: '2px'
            }}>
              {gift.name}
            </div>
            <div style={{
              fontSize: '10px',
              color: selectedGift.id === gift.id ? 'rgba(255,255,255,0.9)' : '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px'
            }}>
              <img src={CDN + 'gold.png'} alt="" style={{ width: '10px' }} />
              {formatCost(gift.cost)}
            </div>
          </animated.div>
        ))}
      </div>

      {/* Quantity Selector */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          fontSize: '12px',
          color: '#666',
          marginBottom: '8px',
          fontWeight: '500'
        }}>
          Select Quantity
        </div>
        <div style={{
          display: 'flex',
          gap: '8px'
        }}>
          {QUANTITIES.map(q => (
            <button
              key={q}
              onClick={() => setQuantity(q)}
              style={{
                flex: 1,
                padding: '10px 4px',
                border: 'none',
                borderRadius: '8px',
                background: quantity === q
                  ? 'linear-gradient(135deg, #667eea, #764ba2)'
                  : '#f0f0f0',
                color: quantity === q ? 'white' : '#333',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {q >= 1000 ? (q/1000) + 'K' : q}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Gift Preview with Animation */}
      <div style={{
        position: 'relative',
        height: '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px'
      }}>
        <animated.div style={{
          ...flyAnimation,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '48px' }}>{selectedGift.emoji}</div>
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginTop: '4px'
          }}>
            {selectedGift.name} x {quantity}
          </div>
        </animated.div>

        {/* Celebration Particles */}
        {showCelebration && celebrationParticles.map((style, i) => (
          <animated.div
            key={i}
            style={{
              ...style,
              position: 'absolute',
              fontSize: '20px',
              pointerEvents: 'none'
            }}
          >
            {['✨', '🎉', '💫', '⭐', '🌟', '💖'][i % 6]}
          </animated.div>
        ))}
      </div>

      {/* Send Button */}
      <button
        onClick={throwGift}
        disabled={isSending}
        style={{
          width: '100%',
          padding: '14px',
          border: 'none',
          borderRadius: '12px',
          background: isSending
            ? '#ccc'
            : 'linear-gradient(135deg, #ff416c, #ff4b2b)',
          color: 'white',
          fontSize: '16px',
          fontWeight: '700',
          cursor: isSending ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: isSending
            ? 'none'
            : '0 4px 15px rgba(255, 65, 108, 0.4)',
          transition: 'all 0.2s ease'
        }}
      >
        {isSending ? (
          <>
            <span className="spinner-border spinner-border-sm"></span>
            Sending...
          </>
        ) : (
          <>
            Send {selectedGift.emoji}
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '13px'
            }}>
              <img src={CDN + 'gold.png'} alt="" style={{ width: '12px', marginRight: '4px' }} />
              {totalCost.toLocaleString()}
            </span>
          </>
        )}
      </button>

      {/* Recipient Info */}
      {localStorage.getItem('user') && (
        <div style={{
          marginTop: '12px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#888'
        }}>
          Sending to <strong>{localStorage.getItem('user')}</strong>
        </div>
      )}
    </div>
  );
}
